import React from "react";
import BillSupplyHeader from "./BillSupplyHeader";
import { formatPatientName } from "../utils/patientName";

export interface BillPrintLine {
  key: string;
  type: string;
  description: string;
  batchNo?: string;
  expiryDate?: string;
  quantity: number;
  unitPrice: number;
  totalAmount: number;
}

interface BillPrintPagesProps {
  billId?: number;
  contact?: { firstName?: string; lastName?: string; name?: string; salutation?: string; dob?: string; gender?: string; phone?: string; address?: string };
  date: string;
  billedBy: string;
  lines: BillPrintLine[];
  totalAmount: number;
}

const calculateAge = (dob?: string) => {
  if (!dob) return "";
  const birthDate = new Date(dob);
  if (Number.isNaN(birthDate.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;
  return String(age);
};

const formatExpiry = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value.replace(/\//g, "-") : date.toLocaleDateString("en-GB", { month: "2-digit", year: "numeric" });
};

const BillPrintPages: React.FC<BillPrintPagesProps> = ({ billId, contact, date, billedBy, lines, totalAmount }) => {
  const pages: BillPrintLine[][] = [];
  for (let index = 0; index < lines.length; index += 12) pages.push(lines.slice(index, index + 12));
  if (!pages.length) pages.push([]);
  return <>
                {pages.map((pageLines, pageIndex) => (
                  <section
                    key={`bill-page-${pageIndex}`}
                    className={`flex min-h-[1120px] flex-col bg-white p-6 shadow print:box-border print:h-[287mm] print:min-h-0 print:w-[210mm] print:p-[10mm] print:shadow-none print:overflow-hidden ${
                      pageIndex === pages.length - 1 ? "print:break-after-auto" : "print:break-after-page"
                    }`}
                  >
                    <BillSupplyHeader documentTitle={billId ? `Bill of Supply - PR-INV-${billId}` : "Bill of Supply"} />

                    <div className="grid grid-cols-3 gap-3 border-b-2 border-[#2d2a78] py-3 text-sm font-bold text-[#2d2a78]">
                      <div>Name: <span className="font-semibold text-text-light">{formatPatientName(contact)}</span></div>
                      <div>Age/Gender: <span className="font-semibold text-text-light">{calculateAge(contact?.dob) || "-"} / {contact?.gender || "-"}</span></div>
                      <div>Date: <span className="font-semibold text-text-light">{date}</span></div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 border-b-2 border-[#2d2a78] py-3 text-sm font-bold text-[#2d2a78]">
                      <div>Phone: <span className="font-semibold text-text-light">{contact?.phone || "-"}</span></div>
                      <div>Billed By: <span className="font-semibold text-text-light">{billedBy}</span></div>
                      <div className="col-span-2">Address: <span className="font-semibold text-text-light">{contact?.address || "-"}</span></div>
                    </div>

                    <div className="flex-1 pt-4">
                      <table className="w-full table-fixed border-collapse text-sm">
                        <colgroup>
                          <col className="w-[8%]" />
                          <col className="w-[34%]" />
                          <col className="w-[13%]" />
                          <col className="w-[13%]" />
                          <col className="w-[8%]" />
                          <col className="w-[12%]" />
                          <col className="w-[12%]" />
                        </colgroup>
                        <thead className="bg-[#2d2a78] text-white">
                          <tr>
                            <th className="border p-2 text-left">Sl. No.</th>
                            <th className="border p-2 text-left">Particulars</th>
                            <th className="border p-2 text-left">Batch No</th>
                            <th className="border p-2 text-left">Expiry Date</th>
                            <th className="border p-2 text-center">Qty</th>
                            <th className="border p-2 text-right">Unit Price</th>
                            <th className="border p-2 text-right">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pageLines.length ? pageLines.map((line, index) => (
                            <tr key={line.key}>
                              <td className="border p-2">{pageIndex * 12 + index + 1}</td>
                              <td className="border p-2">
                            <div className="font-semibold">{line.description}</div>
                              </td>
                              <td className="border p-2 text-center">{line.type === "medicine" ? line.batchNo || "-" : "-"}</td>
                              <td className="border p-2 text-center">{line.type === "medicine" ? formatExpiry(line.expiryDate) : "-"}</td>
                              <td className="border p-2 text-center">{line.quantity}</td>
                              <td className="border p-2 text-right">Rs {line.unitPrice.toFixed(2)}</td>
                              <td className="border p-2 text-right">Rs {line.totalAmount.toFixed(2)}</td>
                            </tr>
                          )) : (
                            <tr>
                              <td colSpan={7} className="border p-3 text-center text-subtext-light">No billable items found.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {pageIndex === pages.length - 1 && (
                        <div className="mt-4 flex justify-end text-xl font-bold">Grand Total: Rs {totalAmount.toFixed(2)}</div>
                      )}
                    </div>

                  </section>
                ))}
  </>;
};

export default BillPrintPages;
